import { useState, useEffect } from 'react'
import useApi from '../api';
import { useParams, useNavigate } from 'react-router-dom';
import Sessions from '../sessions/SessionsList';
import { formatDate } from '../utils/dateFormatter';
import { Table, TableHead, TableRow, TableBody, TableHeader, TableCell } from '@/components/ui/table';
import { Button } from "@/components/ui/button";
import SalesforceIcon from '../icons/salesforce_logo.png'
import HubspotIcon from '../icons/hubspot_logo.webp'
import AttioIcon from '../icons/attio_logo.png'
import GoogleAdsIcon from '../icons/google_ads_logo.png'
import RowDelete from '../ui/RowDelete';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Eye, MessagesSquare, Plug } from "lucide-react";
import React from 'react';
import Assessments from '../assessments/Assessments';
import Reports from '../reports/Reports';
import { VisitorJourney } from '../journey';
import VisitorIdentity from '../VisitorIdentity';
import ContactObjectives from './ContactObjectives';
import ProactiveMessages from './ProactiveMessages';
import SectionHeader from '@/components/ui/SectionHeader';

interface Lead {
    id: string;
    first_name: string;
    last_name: string;
    company: string;
    email: string;
    job_title: string;
    agent_id?: string;
    visitor_id?: string;
    attributes: any;
    created_at: string;
    email_verified: boolean;
    identity_verified_at?: string | null;
    source?: string;
    external_id?: string;
    integrations?: {
        salesforce?: string;
        salesforce_error?: string;
        salesforce_contact?: string;
        salesforce_contact_error?: string;
        salesforce_account?: string;
        salesforce_account_error?: string;
        hubspot?: string;
        hubspot_error?: string;
        attio?: string;
        attio_error?: string;
        attio_company?: string;
        attio_company_error?: string;
        google_ads_conversion_status?: string;
        google_ads_conversion_error?: string;
        google_ads_error?: string;
    };
}

export default function Lead() {
    const { id } = useParams();
    const [, setIsOpen] = useState(false)
    const { get, remove } = useApi();
    const [lead, setLead] = useState<Lead | undefined>();
    const [conversations, setConversations] = useState<any[]>([]);
    const [, setInteracted] = useState<boolean>(false);
    const [message] = useState<any>(null);
    const navigate = useNavigate();

    const crmSyncRows = React.useMemo(() => {
        if (!lead?.integrations) return [];

        type CrmKey = 'salesforce' | 'hubspot' | 'attio';
        type CrmRow = {
            object: string;
            crm: CrmKey;
            url?: string;
            error?: string;
            icon: string;
        };

        const rows: CrmRow[] = [];
        const i = lead.integrations;

        const pushRow = (row: CrmRow) => {
            if (row.url || row.error) rows.push(row);
        };

        pushRow({
            object: "Lead",
            crm: "salesforce",
            url: i.salesforce,
            error: i.salesforce_error,
            icon: SalesforceIcon,
        });
        pushRow({
            object: "Contact",
            crm: "salesforce",
            url: i.salesforce_contact,
            error: i.salesforce_contact_error,
            icon: SalesforceIcon,
        });
        pushRow({
            object: "Account",
            crm: "salesforce",
            url: i.salesforce_account,
            error: i.salesforce_account_error,
            icon: SalesforceIcon,
        });
        pushRow({
            object: "Contact",
            crm: "hubspot",
            url: i.hubspot,
            error: i.hubspot_error,
            icon: HubspotIcon,
        });
        pushRow({
            object: "Person",
            crm: "attio",
            url: i.attio,
            error: i.attio_error,
            icon: AttioIcon,
        });
        pushRow({
            object: "Company",
            crm: "attio",
            url: i.attio_company,
            error: i.attio_company_error,
            icon: AttioIcon,
        });

        return rows;
    }, [lead]);

    function openModal() {
        setIsOpen(true)
    }

    useEffect(() => {
        if (id) {
            get(`/leads/${id}`).then((response) => {
                setLead(response.data);
            });
            get(`/leads/${id}/conversations`).then((response) => {
                setConversations(response.data);
            });
        }
    }, []);

    const hasInteracted = () => {
        for (let conversation of conversations) {
            for (let message of conversation.messages) {
                if (message.role === "user" && (message.content !== "" && message.content !== null)) {
                    setInteracted(true);
                }
            }
        }
    };

    useEffect(() => {
        hasInteracted();
    }, [conversations]);

    useEffect(() => {
        if (message) {
            openModal();
        }
    }, [message]);

    const handleDelete = async () => {
        try {
            await remove(`/leads/${id}`);
            navigate('/contacts');
            toast.success('Contact deleted successfully', toastConfig);
        } catch (error) {
            console.error('Error deleting Lead:', error);
            toast.error('Failed to delete lead', toastConfig);
        }
    };

    const hasIntegrations = Boolean(
        crmSyncRows.length
        || lead?.integrations?.google_ads_conversion_status
        || lead?.integrations?.google_ads_error
    );

    return (
        <>
            {lead && (
                <div className="flex w-full h-full overflow-y-auto scrollbar-thin">
                    <div className="w-full max-w-7xl mx-auto p-8 space-y-6">
                        {/* Header Section */}
                        <div className="flex justify-between items-center">
                            <h1 className="text-3xl font-bold">Contact Profile</h1>
                            <RowDelete deleteCallback={handleDelete} />
                        </div>
                        <VisitorIdentity leadId={id} lead={lead} clickable={false} mode="full" showLeadEvaluation contactDetailHeadings />

                        {id && <ContactObjectives leadId={id} />}
                        {id && <ProactiveMessages leadId={id} />}

                        {/* Integrations & Meta */}
                        {hasIntegrations && <div className="space-y-2">
                            <SectionHeader title="Integrations" icon={Plug} />
                            <div className="space-y-4">
                            {crmSyncRows.length > 0 && (
                                <div className="rounded-xl border bg-card overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[40%]">Object</TableHead>
                                                <TableHead>View</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {crmSyncRows.map((row) => {
                                                const hasUrl = Boolean(row.url);
                                                const hasError = Boolean(row.error);
                                                const crmLabel =
                                                    row.crm === "salesforce"
                                                        ? "Salesforce"
                                                        : row.crm === "hubspot"
                                                            ? "HubSpot"
                                                            : "Attio";
                                                const buttonClass = cn(
                                                    hasError && "border-red-500 text-red-600 hover:text-red-600"
                                                );

                                                const viewButton = hasUrl ? (
                                                    <Button variant="outline" size="sm" asChild className={buttonClass}>
                                                        <a href={row.url} target="_blank" rel="noopener noreferrer">
                                                            <Eye className="h-4 w-4" />
                                                            View
                                                        </a>
                                                    </Button>
                                                ) : (
                                                    <Button variant="outline" size="sm" disabled className={buttonClass}>
                                                        <Eye className="h-4 w-4" />
                                                        View
                                                    </Button>
                                                );

                                                return (
                                                    <TableRow key={`${row.crm}-${row.object}`}>
                                                        <TableCell className="font-medium">{row.object}</TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <span>{viewButton}</span>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            {hasError ? `Error: ${row.error}` : `View in ${crmLabel}`}
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                                <img
                                                                    src={row.icon}
                                                                    alt={crmLabel}
                                                                    className="h-5 w-5 object-contain"
                                                                />
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}

                            {(lead.integrations?.google_ads_conversion_status || lead.integrations?.google_ads_error) && (
                                <div className="flex items-center gap-2">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    className={cn(
                                                        "p-2 rounded-lg border bg-card",
                                                        (lead.integrations?.google_ads_conversion_status === "failed" || lead.integrations?.google_ads_error) && "border-red-500",
                                                        lead.integrations?.google_ads_conversion_status === "success" && "border-green-500"
                                                    )}
                                                >
                                                    <img src={GoogleAdsIcon} alt="Google Ads" className="w-8 h-8 object-contain" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {(lead.integrations?.google_ads_conversion_status === "failed" || lead.integrations?.google_ads_error)
                                                    ? `Error: ${lead.integrations?.google_ads_conversion_error || lead.integrations?.google_ads_error}`
                                                    : lead.integrations?.google_ads_conversion_status === "success"
                                                        ? 'Conversion Tracked Successfully'
                                                        : 'Status Unknown'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            )}
                            </div>
                        </div>
                        }

                        {/* Assessments and Reports Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Assessments leadId={id} contactDetail />
                            <Reports leadId={id} contactDetail />
                        </div>
                        
                        {/* Visitor Journey Section */}
                        <VisitorJourney leadId={id} contactDetail />
                        
                        {/* Sessions Section */}
                        <div className="space-y-2">
                            <SectionHeader title="Sessions" icon={MessagesSquare} />
                            <div className="rounded-xl border bg-card p-4">
                                <Sessions leadId={id} embedded />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
